// ─── Result ──────────────────────────────────────────────────────────────────

import { DEFAULT_CUTOFF_MINUTES, DEFAULT_WINDOWS } from '../constants/windows';

export type DrawResult = {
  id: string;
  houseId: string;
  window: string;
  first: string;
  second: string;
  third: string;
  source: string;
  publishedAt: number;
  dayKey?: string;
  feed?: string;
};

export function createResult(
  fields: Partial<DrawResult> & { dayKey?: string; feed?: string },
): DrawResult {
  return {
    id:          fields.id          ?? crypto.randomUUID(),
    houseId:     fields.houseId     ?? '',
    window:      fields.window      ?? 'day',
    first:       fields.first       ?? '',
    second:      fields.second      ?? '',
    third:       fields.third       ?? '',
    source:      fields.source      ?? 'classic',
    publishedAt: fields.publishedAt ?? Date.now(),
    ...(fields.dayKey != null ? { dayKey: String(fields.dayKey) } : {}),
    ...(fields.feed != null ? { feed: String(fields.feed) } : {}),
  };
}

// ─── House ───────────────────────────────────────────────────────────────────

export type HouseConfigFields = {
  dayOpen: string;
  dayClose: string;
  nightOpen: string;
  nightClose: string;
  cutoffMin: number;
  perNumberCap: number;
  source: string;
};

export type House = {
  id: string;
  name: string;
  bankerId: string;
  status: 'active' | 'suspended' | string;
  config: HouseConfigFields;
  createdAt: number;
};

export function createHouse(fields: Partial<House> & { config?: Partial<HouseConfigFields> } = {}): House {
  return {
    id:       fields.id       ?? crypto.randomUUID(),
    name:     fields.name     ?? '',
    bankerId: fields.bankerId ?? '',
    status:   fields.status   ?? 'active',
    config: {
      dayOpen:      fields.config?.dayOpen      ?? DEFAULT_WINDOWS.day.open,
      dayClose:     fields.config?.dayClose     ?? DEFAULT_WINDOWS.day.close,
      nightOpen:    fields.config?.nightOpen    ?? DEFAULT_WINDOWS.night.open,
      nightClose:   fields.config?.nightClose   ?? DEFAULT_WINDOWS.night.close,
      cutoffMin:    fields.config?.cutoffMin    ?? DEFAULT_CUTOFF_MINUTES,
      perNumberCap: fields.config?.perNumberCap ?? 5000,
      source:       fields.config?.source       ?? 'florida',
    },
    createdAt: fields.createdAt ?? Date.now(),
  };
}

// ─── Notification ─────────────────────────────────────────────────────────────

export type NotifCategory =
  | 'cutoff' | 'results' | 'limits' | 'routes' | 'users'
  | 'messages' | 'system' | 'summary' | 'integrity';
export type NotifSeverity = 'info' | 'success' | 'warn' | 'danger';

export type AppNotification = {
  id: string;
  cat: NotifCategory | string;
  sev: NotifSeverity | string;
  title: string;
  body: string;
  unread: boolean;
  urgent: boolean;
  t: string;
  createdAt: number;
  expiresAt?: number;
  meta?: Record<string, unknown>;
};

export function createNotification(fields: Partial<AppNotification> = {}): AppNotification {
  return {
    id:        fields.id        ?? crypto.randomUUID(),
    cat:       fields.cat       ?? 'system',
    sev:       fields.sev       ?? 'info',
    title:     fields.title     ?? '',
    body:      fields.body      ?? '',
    unread:    fields.unread    ?? true,
    urgent:    fields.urgent    ?? false,
    t:         fields.t         ?? new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    createdAt: fields.createdAt ?? Date.now(),
    expiresAt: fields.expiresAt ?? undefined,
    meta:      fields.meta      ?? undefined,
  };
}
